import I18n from "@/plugins/i18n"
import User from "@/managers/user/user"
import {watch} from "@/store/store"
import Notifications from "@/managers/session/notifications"
import Configuration from "@/managers/system/configuration"
import type {TranslateResult, Values} from "vue-i18n"
import {Duration} from "luxon"

class Localization {
	public get current(): string {
		return I18n.locale
	}
	public set current(value: string) {
		if (User.isReady &&!(Configuration.localization.hiddenLanguages.includes(value) || Configuration.localization.hiddenLanguages.includes(this.current)))
			User.setLanguage(value)
				.catch(reason => Notifications.error("Failed to change language", reason))
		else
			I18n.locale = value
	}

	public get decimalPoint(): "." | "," {
		return this.current === "en" ? "." : ","
	}

	public get languages(): string[] {
		return User.isReady && User.current!.hasBackofficeAccess || Configuration.localization.hiddenLanguages.length === 0
			? I18n.availableLocales
			: I18n.availableLocales.filter(locale => !Configuration.localization.hiddenLanguages.includes(locale))
	}

	public get languageNames(): {[key: string]: string} {
		return Configuration.localization.names
	}

	constructor() {
		watch(() => User.current?.language ?? null, language => {
			if (language !== null)
				I18n.locale = language
		})

		watch(
			() => I18n.locale,
			language => {
				try {
					window.document.documentElement.setAttribute("lang", language)
					window.document.documentElement.setAttribute("xml:lang", language)
					document.head.querySelector("meta[http-equiv=\"Content-Language\"]")!.setAttribute("content", language)
				} catch (error: any) {
					Notifications.warning("Failed to update language attributes", error)
				}
			},
			true
		)
	}

	public exists(path: string): boolean {
		return I18n.te(path)
	}

	public translate(path: string, values?: Values): TranslateResult {
		return I18n.t(path, values)
	}

	public getNumber(value: number, format: string, locale?: string): TranslateResult {
		return I18n.n(value, format, locale ?? this.current)
	}

	public getCurrency(value: number, currency: string, useSymbol: boolean, removeFraction: boolean = false): TranslateResult {
		return I18n.n(value, {
			key: "currency",
			currency,
			currencyDisplay: useSymbol ? "narrowSymbol" : "code",
			maximumSignificantDigits: removeFraction ? Math.trunc(Math.abs(value)).toFixed().length : undefined
		} as Intl.NumberFormatOptions)
	}

	public getCurrencyFormat(currency: string, useSymbol: boolean) : Intl.NumberFormat {
		const currencyFormat = I18n.getNumberFormat(I18n.locale)["currency"]
		currencyFormat.currency = currency
		currencyFormat.currencyDisplay = useSymbol ? "narrowSymbol" : "code"

		return Intl.NumberFormat(this.current, currencyFormat)
	}

	public getHumanDurationFromSeconds(seconds: number): TranslateResult {
		let duration = Duration.fromObject({seconds}, {locale: this.current})

		if (seconds > 90)
			duration = duration.rescale()

		return this.getHumanDuration(duration)
	}

	public getHumanDuration(duration: Duration): TranslateResult {
		return duration.toHuman()
	}
}

export {TranslateResult, Values}
export default new Localization()
